import { Box, Typography } from '@mui/material';

import { Seller } from 'shared/types/Sellers';

import {
  isLightColor,
  isWhiteColor,
} from '../../../utils/colorUtils/colorUtils';

interface SellerLogoProps {
  size?: number;
  seller?: Seller;
  logo?: string;
  brandColor?: string;
  title?: string;
}

/**
 * A component for generating a graphical representation of a seller's logo.
 *
 * @example
 * // Basic usage with a seller object
 * <SellerLogo seller={seller} size={24} />
 *
 * @example
 * // Use without a seller object
 * <SellerLogo
 *   logo="https://example.com/logo.png"
 *   brandColor="0000FF"
 *   title="Seller Co."
 *   size={24}
 * />
 */
export const SellerLogo = ({
  size = 14,
  seller,
  logo: propLogo,
  brandColor: propBrandColor,
  title: propTitle,
}: SellerLogoProps) => {
  const logo = propLogo || seller?.logo;
  const brandColor = propBrandColor || seller?.brandColor || 'FFF';
  const title = propTitle || seller?.title || '';

  const isLightBrandColor = isLightColor(brandColor);
  const isWhiteBrandColor = isWhiteColor(brandColor);
  const imageSize = (8 * size) / 14;
  const textSize = (9 * size) / 14;

  return (
    <Box
      role="img"
      aria-label={`${title} logo`}
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        boxSizing: 'border-box',
        backgroundColor: `#${brandColor}`,
        borderRadius: '50%',
        border: '1px solid',
        borderColor: isWhiteBrandColor ? '#D9D9D9' : '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {logo ? (
        <img
          src={logo}
          alt={title}
          style={{
            maxWidth: `${imageSize}px`,
            maxHeight: `${imageSize}px`,
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: `${textSize}px`,
            fontWeight: 800,
            lineHeight: `${size}px`,
            textAlign: 'center',
            color: isLightBrandColor ? '#535353' : '#FFFFFF',
          }}
        >
          {title.slice(0, 1).toUpperCase()}
        </Typography>
      )}
    </Box>
  );
};
